import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Stack, TextField, Typography
} from '@mui/material';
import { API_BASE_URL, fetchJson } from './api';

const emptyFeedback = () => ({
    section: '', issue_type: 'incorrect_fact', severity: 'material',
    problem: '', expected_result: '', evidence_reference: ''
});
const issueTypes = [
    ['incorrect_fact', 'Incorrect fact'], ['unsupported_claim', 'Unsupported claim'],
    ['omission', 'Missing information'], ['contradiction', 'Contradiction'],
    ['risk_rating', 'Wrong risk rating'], ['stale_evidence', 'Stale evidence'],
    ['wording', 'Wording'], ['formatting', 'Formatting'], ['acceptable', 'Acceptable example']
];

export function canViewReportDiagnostics(mode, user) {
    return mode === 'surveyor' && !user?.portal && String(user?.role || '').trim().toLowerCase() === 'admin';
}

export default function ReportDiagnostics({ isAdmin, reference, reportInstance, authHeaders }) {
    const [open, setOpen] = useState(false);
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState('');
    const [record, setRecord] = useState(null);
    const [feedback, setFeedback] = useState(emptyFeedback);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const epoch = useRef(0);
    const endpoint = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/report-diagnostics`;
    const query = `?report_instance=${encodeURIComponent(reportInstance)}`;

    useEffect(() => {
        const current = ++epoch.current;
        setRecord(null);
        setError('');
        setNotice('');
        setRuns([]);
        setSelected('');
        setFeedback(emptyFeedback());
        if (!open || !isAdmin || !reference) return undefined;
        setBusy(true);
        fetchJson(`${endpoint}${query}`, { headers: authHeaders(), cache: 'no-store' })
            .then((result) => {
                if (epoch.current !== current) return;
                if (!Array.isArray(result.runs)) throw new Error('Invalid generation record response');
                setRuns(result.runs);
                setSelected(result.runs[0]?.id || '');
            })
            .catch((failure) => {
                if (epoch.current === current) setError(failure.message || 'Could not load generation records');
            })
            .finally(() => {
                if (epoch.current === current) setBusy(false);
            });
        return () => { epoch.current += 1; };
    }, [open, isAdmin, reference, endpoint, query, authHeaders]);

    useEffect(() => {
        let cancelled = false;
        setRecord(null);
        setFeedback(emptyFeedback());
        if (!selected || !open || !isAdmin) return undefined;
        fetchJson(`${endpoint}/${selected}${query}`, { headers: authHeaders(), cache: 'no-store' })
            .then((result) => { if (!cancelled) setRecord(result); })
            .catch((failure) => { if (!cancelled) setError(failure.message || 'Could not load this generation record'); });
        return () => { cancelled = true; };
    }, [selected, open, isAdmin, endpoint, query, authHeaders]);

    async function download(ids) {
        const current = epoch.current;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const response = await fetch(`${endpoint}/download`, {
                method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ report_instance: reportInstance, run_ids: ids }), cache: 'no-store'
            });
            if (!response.ok) {
                const result = await response.json();
                throw new Error(result.error || 'Could not download the diagnostic pack');
            }
            if (!response.headers.get('content-type')?.includes('application/zip')) {
                throw new Error('The server did not return a diagnostic ZIP');
            }
            const blob = await response.blob();
            if (epoch.current !== current) return;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            try {
                link.href = url;
                link.download = `report-diagnostics-${reference.replace(/[^a-z0-9_-]/gi, '_')}-${ids.length}-runs.zip`;
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
            setNotice('Diagnostic pack downloaded. Review its confidential contents before sharing.');
        } catch (failure) {
            if (epoch.current === current) setError(failure.message || 'Could not download the diagnostic pack');
        } finally {
            if (epoch.current === current) setBusy(false);
        }
    }

    async function saveFeedback() {
        const current = epoch.current;
        const runId = selected;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await fetchJson(`${endpoint}/${runId}/feedback`, {
                method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ report_instance: reportInstance, feedback })
            });
            const result = await fetchJson(`${endpoint}/${runId}${query}`, { headers: authHeaders(), cache: 'no-store' });
            if (epoch.current !== current) return;
            setRecord(result);
            setFeedback(emptyFeedback());
            setNotice('Review notes saved. They will be included in the downloaded pack.');
        } catch (failure) {
            if (epoch.current === current) setError(failure.message || 'Could not save review notes');
        } finally {
            if (epoch.current === current) setBusy(false);
        }
    }

    if (!isAdmin) return null;
    return (
        <>
            <Button size="small" variant="outlined" onClick={() => setOpen(true)} disabled={!reference}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Generation diagnostics
            </Button>
            <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="md">
                <DialogTitle>Generation diagnostics - admin only</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning">
                            Packs contain confidential project information, prompts and model outputs. They are not anonymised.
                            Authentication fields are redacted, but review the contents before sharing.
                        </Alert>
                        <Typography variant="body2">
                            Choose a generation, describe good or flawed sections, then download a ZIP.
                            The latest 10 option bundles up to 10 runs for this project and reporting period.
                        </Typography>
                        {error && <Alert severity="error">{error}</Alert>}
                        {notice && <Alert severity="success">{notice}</Alert>}
                        {busy && <CircularProgress size={24} aria-label="Loading diagnostics" />}
                        {!busy && !runs.length && (
                            <Alert severity="info">
                                No generation records are available. Historical reports cannot be reconstructed;
                                generate a new report after this feature is deployed to capture a diagnostic pack.
                            </Alert>
                        )}
                        {runs.length > 0 && (
                            <>
                                <TextField select label="Generation record" value={selected} disabled={busy}
                                    onChange={(event) => { setSelected(event.target.value); setError(''); setNotice(''); }}>
                                    {runs.map((run) => (
                                        <MenuItem key={run.id} value={run.id}>
                                            {new Date(run.started_at).toLocaleString()} - {run.status} - {run.id.slice(0, 8)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {record?.run && (
                                    <>
                                        {(record.run.status !== 'completed' || record.run.capture_errors?.length > 0) && (
                                            <Alert severity="warning">
                                                This generation is {record.run.status} or has capture errors. Its pack may be incomplete;
                                                check the manifest for missing data.
                                            </Alert>
                                        )}
                                        <Typography variant="body2">
                                            {record.events?.length || 0} captured events; {record.feedback?.length || 0} saved reviews.
                                            {' '}Run ID: {record.run.id}
                                        </Typography>
                                        <Box sx={{ maxHeight: 180, overflow: 'auto' }} aria-label="Generation timeline">
                                            {(record.events || []).map((event) => (
                                                <Typography key={event.sequence} variant="caption" component="div">
                                                    {new Date(event.recorded_at).toLocaleTimeString()} - {event.kind.replace(/_/g, ' ')}
                                                </Typography>
                                            ))}
                                        </Box>
                                        {(record.feedback || []).map((review) => (
                                            <Alert key={review.id} severity="info">
                                                {review.feedback.section}: {review.feedback.problem}
                                            </Alert>
                                        ))}
                                    </>
                                )}
                                <Typography variant="subtitle2">Add review notes to this generation</Typography>
                                <TextField label="Section or passage" value={feedback.section} disabled={busy}
                                    inputProps={{ maxLength: 12000 }}
                                    onChange={(event) => setFeedback({ ...feedback, section: event.target.value })} />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField select fullWidth label="Issue type" value={feedback.issue_type} disabled={busy}
                                        onChange={(event) => setFeedback({ ...feedback, issue_type: event.target.value })}>
                                        {issueTypes.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                                    </TextField>
                                    <TextField select fullWidth label="Severity" value={feedback.severity} disabled={busy}
                                        onChange={(event) => setFeedback({ ...feedback, severity: event.target.value })}>
                                        {['cosmetic', 'material', 'critical'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                                    </TextField>
                                </Stack>
                                {[
                                    ['problem', 'What is wrong or acceptable?'],
                                    ['expected_result', 'Expected result or corrected wording'],
                                    ['evidence_reference', 'Supporting document, page or questionnaire field']
                                ].map(([key, label]) => (
                                    <TextField key={key} label={label} multiline minRows={2} value={feedback[key]} disabled={busy}
                                        inputProps={{ maxLength: 12000 }}
                                        onChange={(event) => setFeedback({ ...feedback, [key]: event.target.value })} />
                                ))}
                                <Button onClick={saveFeedback} disabled={busy || !selected || !feedback.section.trim() || !feedback.problem.trim()}>
                                    Save review notes
                                </Button>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Button onClick={() => setOpen(false)} disabled={busy}>Close</Button>
                    <Button onClick={() => download(runs.slice(0, 10).map((run) => run.id))} disabled={busy || !runs.length}>
                        Download latest {Math.min(10, runs.length) || 10} runs
                    </Button>
                    <Button variant="contained" onClick={() => download([selected])} disabled={busy || !selected}>
                        Download selected pack
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
